import React, { useState } from "react";
import styles from "./fba-listings-list-item.module.css";
import { FbaListing, useFbaListingContext } from "@/contexts/FbaListingContext";
import { useInventoryContext } from "@/contexts/InventoryContext";
import ProductImage from "@/components/ProductImage/ProductImage";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconMore from "@/components/icons/IconMore";
import dayjs from "dayjs";
import { ProductCondition } from "@boxem/core-api-schema";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import Flex from "@/components/Flex/Flex";
import { productConditionToString } from "@/app/fba/utils";
import ShipmentDropdown from "@/components/ShipmentDropdown/ShipmentDropdown";
import CopyToClipBoard from "@/components/CopyToClipboard/CopyToClipboard";

type FbaListingsListItemProps = {
  listing: FbaListing;
  onEditItemClicked?: (listing: FbaListing) => void;
  onlyAsinAnsSku?: boolean;
};

const FbaListingsListItem = ({
  listing,
  onlyAsinAnsSku,
  onEditItemClicked,
}: FbaListingsListItemProps) => {
  const { fbaListings, deleteListingFromList } = useFbaListingContext();
  const { suppliers } = useInventoryContext();
  const [confirmDeleteItemModalOpen, setConfirmDeleteItemModalOpen] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const supplier = suppliers.find(sup => sup.name === listing.supplier);

  const handleOnClose = (confirmed: boolean) => {
    //Remove fba listing.
    if (confirmed) {
      const listingIndex = fbaListings.findIndex(l => l.sku === listing.sku);

      if (listingIndex !== -1) {
        setLoadingDelete(true);
        deleteListingFromList(listing.sku);
        setConfirmDeleteItemModalOpen(false);
        setLoadingDelete(false);
      }
    } else {
      setConfirmDeleteItemModalOpen(false);
    }
  };

  const handleOptionSelect = (option: string) => {
    switch (option) {
      case "delete":
        setConfirmDeleteItemModalOpen(true);
        break;
      case "edit":
        onEditItemClicked(listing);
        break;
      default:
    }
  };

  return (
    <>
      <ConfirmModal
        width={"30rem"}
        title={"Delete listing"}
        body={<p style={{margin:"20px"}}>Confirm that you want to delete this listing.</p>}
        loading={loadingDelete}
        open={confirmDeleteItemModalOpen}
        onClose={handleOnClose}
      />

      <div className={styles.container} onClick={() => onEditItemClicked(listing)}>
        <div className={styles.productInfo}>
          <ProductImage className={styles.productImage} imageUrl={listing.image} alt={listing.name} />
          <Flex className={styles.minWidth0} direction="column" gap={3}>
            <Flex className={`${styles.minWidth0} ${styles.itemListContent}`} direction="column">
              <Flex align="center">
                <Tooltip title={listing.name}>
                  <span
                    className={`strong ${styles.itemListTitle}`}
                    style={{textOverflow:"ellipsis",overflow:"hidden",whiteSpace:"nowrap"}}
                  >
                    {listing.name}
                  </span>
                </Tooltip>
                {!!onEditItemClicked && (
                  <ShipmentDropdown
                    align="right"
                    button={
                      <div className={styles.dropdownButtonContainer}>
                        <IconMore size={20} />
                      </div>
                    }
                    onOptionSelect={handleOptionSelect}
                    openOnHover
                    options={[
                      { label: "Delete", value: "delete" },
                      { label: "Edit", value: "edit" },
                    ]}
                  />
                )}
              </Flex>

              <CopyToClipBoard value={listing.asin} tooltipText="Copy ASIN">
                <Flex gap={1} className={styles.listItemRow}>
                  <span className="small-text weak">ASIN:</span>
                  <span className="small-text strong">{listing.asin}</span>
                </Flex>
              </CopyToClipBoard>

              <CopyToClipBoard value={listing.sku} tooltipText="Copy SKU">
                <Flex gap={1} className={styles.listItemRow}>
                  <span className="small-text weak">SKU:</span>
                  <span className="small-text strong">{listing.sku}</span>
                </Flex>
              </CopyToClipBoard>
              {!onlyAsinAnsSku && (
                <>
                  <Flex gap={1} className={styles.listItemRow}>
                    <span className="small-text weak">Condition:</span>
                    <span className="small-text strong">{productConditionToString(listing.condition as ProductCondition)}</span>
                  </Flex>
                  {!!listing.expirationDate && (
                    <Flex gap={1} className={styles.listItemRow}>
                      <span className="small-text weak">Expiration:</span>
                      <span className="small-text strong">{dayjs.utc(listing.expirationDate).format("MM/DD/YYYY")}</span>
                    </Flex>
                  )}
                  {!!listing.supplier && (
                    <Flex gap={1} className={styles.listItemRow}>
                      <span className="small-text weak">Supplier:</span>
                      <span className="small-text strong">{supplier?.name}</span>
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </Flex>
        </div>
      </div>
    </>
  );
};

export default FbaListingsListItem;